import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useAtomValue, useSetAtom } from 'jotai';
import type { ReactNode } from 'react';

import { dismissToastAtom, toastsAtom } from '@/store';

export { formatIdentity, UserLabel } from './UserLabel';

/**
 * The small set of shells every page is built from (spec §9.5).
 *
 * MUI v9 rules apply throughout: no style props on `<Typography>`, `slotProps` instead of
 * `InputProps`, `Outlined` icon names.
 */

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Anchor for the page guide (spec §9.5); every header carries one. */
  helpId?: string;
}

/** Page title in Lexend Light, one line of context under it, the page's actions on the right. */
export function PageHeader({ title, subtitle, actions, helpId }: PageHeaderProps) {
  return (
    <Box
      data-help={helpId}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 3,
        mb: 3,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h1" sx={{ mb: subtitle ? 0.5 : 0 }}>
          {title}
        </Typography>
        {typeof subtitle === 'string' ? (
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{subtitle}</Typography>
        ) : (
          subtitle
        )}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
}

export interface SectionCardProps {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  helpId?: string;
  /** Removes the padding, for a card whose child is a table or a virtualised list. */
  flush?: boolean;
}

export function SectionCard({
  title,
  description,
  actions,
  children,
  helpId,
  flush = false,
}: SectionCardProps) {
  return (
    <Paper data-help={helpId} variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      {(title || actions) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2.5,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title ? <Typography variant="h3">{title}</Typography> : null}
            {description ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          {actions ? (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {actions}
            </Stack>
          ) : null}
        </Box>
      )}
      <Box sx={flush ? undefined : { p: 2.5 }}>{children}</Box>
    </Paper>
  );
}

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Anchor for the page guide (spec §9.5), rendered as `data-help` on the field. */
  helpId?: string;
}

export function SearchField({ value, onChange, placeholder, autoFocus, helpId }: SearchFieldProps) {
  return (
    <TextField
      data-help={helpId}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder ?? 'Search'}
      autoFocus={autoFocus}
      sx={{ minWidth: 240 }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export interface FilterBarProps {
  children: ReactNode;
  /** Rendered on the right; typically a count and a "clear filters" button. */
  trailing?: ReactNode;
  helpId?: string;
}

export function FilterBar({ children, trailing, helpId }: FilterBarProps) {
  return (
    <Box
      data-help={helpId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        px: 2,
        py: 1.5,
        mb: 2,
        borderRadius: '10px',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
        {children}
      </Box>
      {trailing}
    </Box>
  );
}

export interface AppDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Shown above the content, for the reason a submit was refused. */
  error?: string | null;
}

export function AppDialog({
  open,
  title,
  onClose,
  children,
  actions,
  maxWidth = 'sm',
  error,
}: AppDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {children}
      </DialogContent>
      {actions ? <DialogActions>{actions}</DialogActions> : null}
    </Dialog>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 5,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Halo size={72} sx={{ mb: 1.5 }} />
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ color: 'text.secondary', mb: action ? 2 : 0, maxWidth: 520 }}>
          {description}
        </Typography>
      ) : null}
      {action}
    </Box>
  );
}

/**
 * The one ornament of the design language: a ring of dots fading outward, taken from the Spectrum
 * logo. Blue in the product (empty states, loading, sign-in); never behind text.
 */
export function Halo({
  size = 104,
  dot = 13,
  opacity = 1,
  sx,
}: {
  size?: number;
  /** Distance between dot centres. */
  dot?: number;
  opacity?: number;
  sx?: SxProps<Theme>;
}) {
  const ring = 'radial-gradient(circle, transparent 30%, #000 48%, transparent 72%)';
  // The caller's sx goes on the outer box (position, margin, animation); the pattern on the inner.
  return (
    <Box aria-hidden="true" sx={sx}>
      <Box
        sx={(theme) => ({
          width: size,
          height: size,
          opacity,
          backgroundImage: `radial-gradient(circle, ${theme.palette.spectrum.mark} ${dot * 0.17}px, transparent ${dot * 0.215}px)`,
          backgroundSize: `${dot}px ${dot}px`,
          backgroundPosition: 'center',
          WebkitMaskImage: ring,
          maskImage: ring,
        })}
      />
    </Box>
  );
}

/** A full-area wait: the halo breathing, instead of a spinner on an empty page. */
export function PageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <Box
      role="progressbar"
      aria-label={label}
      sx={{ display: 'grid', placeItems: 'center', py: 10 }}
    >
      <Halo
        size={88}
        sx={{
          '@keyframes gls-halo': { '0%, 100%': { opacity: 0.35 }, '50%': { opacity: 0.8 } },
          animation: 'gls-halo 1.6s ease-in-out infinite',
        }}
      />
    </Box>
  );
}

/** One host for every toast; mutations push, this renders (spec §9.4). */
export function ToastHost() {
  const toasts = useAtomValue(toastsAtom);
  const dismiss = useSetAtom(dismissToastAtom);
  const current = toasts[0];

  return (
    <Snackbar
      open={Boolean(current)}
      autoHideDuration={current?.severity === 'error' ? 8000 : 4000}
      onClose={() => current && dismiss(current.id)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      {current ? (
        <Alert
          severity={current.severity}
          onClose={() => dismiss(current.id)}
          sx={{ minWidth: 320 }}
        >
          {current.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  destructive = false,
  busy = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <AppDialog
      open={open}
      title={title}
      onClose={onClose}
      maxWidth="xs"
      actions={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Typography>{message}</Typography>
    </AppDialog>
  );
}
